import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  BannerItem,
  deleteBannerGroup,
  getBannersList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateBannersBunch,
} from "store/cms/banners/slice";
import { bannersListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";

import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import BannerFilterList from "./components/BannerFilter";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import { GuideButton } from "modals/guides/GuideButton";
import { useGuideModal } from "modals/guides/useGuideModal";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";

const BannersList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const { openGuide } = useGuideModal();

  const [order, setOrder] = useState<{
    title: string;
    id: string;
    isActive: string;
  }>({
    title: "none",
    id: "none",
    isActive: "none",
  });

  const [currentLimit, setLimit] = useState<number>(bannersListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [geos, setGeos] = useState<{ value: string | number; label: string }[]>(
    [],
  );
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([]);

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store["cms-banners"].list);
  const permissions = usePermissionHelper();

  const isListFetched = useAppSelector(
    (store) => store["cms-banners"].isListFetched,
  );

  const processingItemId = useAppSelector(
    (state) => state["cms-banners"].processingItemId,
  );

  const totalItems = useAppSelector((store) => store["cms-banners"].totalItems);
  const totalPages = useAppSelector((store) => store["cms-banners"].totalPages);
  const activePage = useAppSelector((store) => store["cms-banners"].activePage);
  const selectedItems = useAppSelector(
    (store) => store["cms-banners"].selectedItems,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cms-banners',
    isListFetched
  });

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getBannersList({
          limit: currentLimit,
          offset: activePage - 1,
          title: search,
          geos: geos.map((el) => el.value as string),
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    try {
      await dispatch(
        updateBannersBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.banners}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.banners }));
  };

  const handleDeleteItem = async (id: number) => {
    const resultTag = await dispatch(deleteBannerGroup(id ?? 0));

    if (resultTag?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };
  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.banners}`);
    dispatch(openModal({ activeModal: ModalTypes.banners }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };

  const preparedList = useMemo(() => {
    return list.map((item) => {
      const { banners, ...listItem } = item;
      const previewFromBanner =
        banners && banners.length > 0
          ? banners[0].imageSmall
          : null;

      return {
        ...listItem,
        image: item.image ?? previewFromBanner ?? null,
      };
    });
  }, [list]);

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <BannerFilterList
        searchValue={search}
        handleSearch={handleFilterSearch}
        selectedActive={active}
        handleUpdateActive={(e) => setActive(e)}
        selectedGeos={geos}
        handleUpdateGeos={(e) => setGeos(e)}
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addBanner")}
                handleClick={() => {
                  handleOpenCreateModal();
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box page-list-header--box-actions">
            <GuideButton onClick={() => openGuide("cms")} />
            <DefaultDropdown
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || !permissions?.update}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={15} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && list.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noBannersFound")} />
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <>
            <div className="game-table-wrap">
              <Table
                data={preparedList as Array<BannerItem & { id: number }>}
                order={order}
                idField="id"
                selectedItems={selectedItems}
                onAllSelect={handleAllClick}
                onSelect={(id) => handleItemClick(id)}
                onDelete={(id) => handleDeleteItem(id)}
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                processingId={processingItemId}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
                
              />
            </div>

            <Pagination
              handleLimitClick={(e) => {
                setLimit(e);
                dispatch(handlePaginate(1));
              }}
              limitsArray={bannersListLimits}
              activePage={activePage}
              limit={currentLimit}
              totalItems={totalItems}
              totalPages={totalPages}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePrevClick}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BannersList;
